import { inject, injectable } from 'inversify'
import type { MessengerWebSocketEventMap } from '@widgets/messenger/types/api/websocket'
import { SERVICE_TYPES } from '@/core/container/types'
import type ChatService from '@/services/domain/chats/chatService'
import type WidgetChatCentrifugeTokenService from '@/services/domain/accounts/WidgetChatCentrifugeTokenService'
import type WidgetChatCentrifugeToken from '@/data/models/domain/WidgetChatCentrifugeToken'
import type { SocketTokenGettingCallbackParams } from '@/services/transport/types'
import ServerEventsSubscriptionService from '@/services/transport/serverEventsSubscriptionService'

import type ServerEventsSubscriptionConnectionBuilderService from '@/services/transport/serverEventsSubscriptionConnectionBuilderService'

@injectable()
export default class WidgetChatServerSubscriptionService {
  constructor(
    @inject(SERVICE_TYPES.ChatService) protected readonly chatService: ChatService,
    @inject(SERVICE_TYPES.WidgetChatCentrifugeTokenService)
    protected readonly widgetChatCentrifugeTokenService: WidgetChatCentrifugeTokenService,
    @inject(SERVICE_TYPES.ServerEventsSubscriptionConnectionBuilderService)
    protected readonly serverEventsSubscriptionConnectionBuilderService: ServerEventsSubscriptionConnectionBuilderService,
  ) {}

  private connectionList: Partial<
    Record<
      string,
      ServerEventsSubscriptionService<
        MessengerWebSocketEventMap,
        Pick<MessengerWebSocketEventMap, 'startTyping' | 'endTyping'>
      >
    >
  > = {}

  public getOrCreateConnectionByChatId(
    chatId: string,
  ): ServerEventsSubscriptionService<
    MessengerWebSocketEventMap,
    Pick<MessengerWebSocketEventMap, 'startTyping' | 'endTyping'>
  > {
    let connection = this.connectionList[chatId]
    if (!connection) {
      connection = new ServerEventsSubscriptionService(async () => {
        return this.serverEventsSubscriptionConnectionBuilderService.createConnectionWithSubscription(
          () => this.getChannelName(chatId),
          () => {
            return this.getTokens(chatId, {})
          },
        )
      })
      this.connectionList[chatId] = connection
    }
    return connection
  }

  public async disconnectIfConnectedByChatId(chatId: string) {
    const connection = this.connectionList[chatId]
    if (!connection) {
      return
    }
    await connection.disconnectIfConnected()
    delete this.connectionList[chatId]
  }

  public disconnectIfConnected() {
    const connections = Object.values(this.connectionList)
    connections.forEach((t) => t?.disconnectIfConnected())
    this.connectionList = {}
  }

  protected async getTokens(
    chatId: string,
    params: SocketTokenGettingCallbackParams,
  ): Promise<WidgetChatCentrifugeToken> {
    const chatToken = this.chatService.getChat(chatId)?.token || ''
    return this.widgetChatCentrifugeTokenService.getTokens(chatId, chatToken, params)
  }

  protected getChannelName(chatId: string) {
    return `web-widget-chat#${chatId}`
  }
}
